import { Box, Button, IconButton, useTheme } from '@mui/material';
import CodeEditor from './CodeEditor';
import { useEffect, useRef, useState } from 'react';
import { API_PATCH, DBRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import TemplatePreview from '../templates/TemplatePreview';
import Loader from '../special/Loader';
import TemplateVariables from './templateEditor/TemplateVariables';
import ExoDialog from '../exo/ExoDialog';
import { useTranslation } from 'react-i18next';
import { convertMetaFieldsToTemplateVars } from '../exo/ExoDocumentPreview';
import TypeText from '../form/form-fields/TypeText';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const TemplateEditor = ({
  templateData,
  onSave = () => {},
  onClose = () => {},
  open = true,
  onFeedback = () => {},
  variables = [],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const aceEditorRef = useRef(null);

  const [template, setTemplate] = useState(templateData);
  const [currentKey, setCurrentKey] = useState('content');
  const [metaData, setMetaData] = useState(templateData.metaData || {});

  useEffect(() => {
    setTemplate(templateData);
    setMetaData(templateData.metaData || {});
  }, [templateData]);

  // ACTIONS --------
  function handleCodeChange(data) {
    setTemplate({
      ...template,
      [currentKey]: data,
    });
  }

  function handleHeightChange(page, headerHeight, footerHeight) {
    var newStyle = JSON.parse(template.styleJson);

    if (
      newStyle.page['headerP' + page + 'Height'] === headerHeight &&
      newStyle.page['footerP' + page + 'Height'] === footerHeight
    )
      return;
    newStyle.page['headerP' + page + 'Height'] = headerHeight;
    newStyle.page['footerP' + page + 'Height'] = footerHeight;

    setTemplate({
      ...template,
      styleJson: JSON.stringify(newStyle),
    });
  }

  function handleSectionSelect(section) {
    setCurrentKey(section);
  }

  const handleClickVariable = (textToInsert) => {
    const currentPos = aceEditorRef.current.editor.getCursorPosition();
    const text = aceEditorRef.current.editor.getValue();
    const beforeText = text.substring(
      0,
      aceEditorRef.current.editor.session.doc.positionToIndex(currentPos)
    );
    const afterText = text.substring(
      aceEditorRef.current.editor.session.doc.positionToIndex(currentPos),
      text.length
    );

    aceEditorRef.current.editor.setValue(beforeText + textToInsert + afterText);
    aceEditorRef.current.editor.clearSelection();
    aceEditorRef.current.editor.moveCursorToPosition(currentPos);
  };

  const config = useConfig();
  function handleSave() {
    DBRequest({
      config,
      path: 'templates/' + template.id,
      method: API_PATCH({ ...template, metaData }),
      onResponse: () => onSave({ ...template, metaData }),
      onFeedback: onFeedback,
    });
    setCurrentKey('content');
    onClose();
  }

  const metaTemplateVars = convertMetaFieldsToTemplateVars(
    metaData?.metaFields,
    true
  );
  const templateVars = template ? template?.vars?.concat(metaTemplateVars) : [];

  const [disableTemplateRendering, setDisableTemplateRendering] =
    useState(false);

  return (
    <ExoDialog open={open}>
      <Box className="flex flex-col h-full w-full">
        {!template.id ? (
          <Loader active={true} message="Template Loading..." />
        ) : (
          <Box className="flex flex-col h-full w-full overflow-y-auto justify-between">
            <Box className="flex flex-row h-full w-full overflow-hidden">
              <TemplateVariables
                className="min-w-[250px] max-w-[200px]"
                variables={template.vars}
                onClick={handleClickVariable}
                metaData={metaData}
                onMetaDataChange={setMetaData}
              />
              <Box className="flex flex-col h-full w-full overflow-hidden pt-2">
                <Box className="flex gap-2 w-full">
                  <TypeText
                    size="small"
                    field={{ label: t('Name'), key: 'name' }}
                    dataCollection={template}
                    updateValidationOnChange={(key, value) =>
                      setTemplate({ ...template, [key]: value })
                    }
                  />
                  <Button
                    color="secondary"
                    variant="outlined"
                    onClick={() =>
                      setTemplate({ ...template, isHidden: !template.isHidden })
                    }
                  >
                    {template.isHidden ? t('Hidden') : t('Visible')}
                  </Button>
                </Box>
                <CodeEditor
                  className="w-full"
                  editorRef={aceEditorRef}
                  data={template[currentKey]}
                  currentKey={currentKey}
                  setData={handleCodeChange}
                  height={500}
                  variables={templateVars}
                  disableTemplateRendering={disableTemplateRendering}
                  changeDisableTemplateRendering={setDisableTemplateRendering}
                />
              </Box>
              <TemplatePreview
                edit={true}
                onSelect={handleSectionSelect}
                data={{ ...template, vars: templateVars }}
                width="300"
                className="m-2 overflow-y-auto"
                paginate={true}
                background={true}
                onChange={handleHeightChange}
                disableVariableRendering={disableTemplateRendering}
              />
            </Box>
            <Box className="flex justify-between p-2">
              <Button
                color="error"
                onClick={() => {
                  setCurrentKey('content');
                  onClose();
                }}
                variant="outlined"
              >
                {t('Cancel')}
              </Button>
              <Button color="success" variant="contained" onClick={handleSave}>
                {t('Save')}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </ExoDialog>
  );
};

export default TemplateEditor;
